import {
  ContactSummary,
  CustomerContactInput,
  CustomerContactsContent,
  PaginatedCustomerContacts
} from 'types/CustomerContactsTypes';
import useHttp from '../helpers/HttpMethods';

class CustomerContactsService {
  /**
   * Busca os contatos do cliente paginados.
   * @param {number} id - ID do cliente.
   * @returns {Promise<PaginatedCustomerContacts>} - Contatos do cliente em formato paginado.
   */
  static async list(
    id: number,
    filter: string
  ): Promise<PaginatedCustomerContacts | Response> {
    return await useHttp().get(`/customers/${id}/contacts?${filter}`);
  }

  static async listPortalUsers(
    id: number,
    filter: string
  ): Promise<PaginatedCustomerContacts | Response> {
    return await useHttp().get(
      `/customers/${id}/contacts/portal-users?${filter}`
    );
  }

  static async searchByNameAndEmail(
    id: number,
    searchTerm: string
  ): Promise<ContactSummary[]> {
    return await useHttp().get(
      `/customers/${id}/contacts/search?${searchTerm}`
    );
  }

  /**
   * Retorna um novo contato para o cliente.
   * @param {CustomerContactInput} body - Modelo para criar um novo contato para o cliente.
   * @returns {Promise<Response | CustomerContactsContent>} - Modelo do objeto retornado na criação.
   */
  static async add(
    body: CustomerContactInput
  ): Promise<Response | CustomerContactsContent> {
    return await useHttp().post(body, `/contacts`);
  }

  /**
   * Edita um contato de cliente.
   * @param {number} id - ID do cliente a ser atualizada.
   * @param {CustomerContactInput} body - Modelo de contato para enviar os dados e atualizar.
   * @returns {Promise<Response | CustomerContactsContent>} - Retorno padrão de uma request ou
   * o contato do cliente atualizado.
   */
  static async edit(
    id: number,
    body: CustomerContactInput
  ): Promise<Response | CustomerContactsContent> {
    return await useHttp().put(body, `/contacts/${id}`);
  }

  /**
   * Associa um cliente a um contato.
   * @param {number} customerId - ID do cliente.
   * @param {number} contactId - ID do contato.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async associate(
    customerId: number,
    contactId: number
  ): Promise<Response> {
    return await useHttp().putWithoutBody(
      `/customers/${customerId}/contacts/${contactId}`
    );
  }

  /**
   * Desassocia um cliente a um contato.
   * @param {number} customerId - ID do cliente.
   * @param {number} contactId - ID do contato.
   * @returns {Promise<Response>} - Retorno padrão de uma request.
   */
  static async unaassociate(
    customerId: number,
    contactId: number
  ): Promise<Response> {
    return await useHttp().del(
      `/customers/${customerId}/contacts/${contactId}`
    );
  }
}

export default CustomerContactsService;
